/*
* Copyright (c) 2026 Send2Me, media capture and delivery platform. All rights reserved.
* Distributed under the TruVideo proprietary license.
*/
import { useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, ChevronLeft, ChevronRight } from 'lucide-react';
import PropTypes from 'prop-types';

const slideVariants = {
  enter: (direction) => ({
    x: direction > 0 ? '100%' : '-100%',
    opacity: 0,
  }),
  center: {
    x: 0,
    opacity: 1,
  },
  exit: (direction) => ({
    x: direction < 0 ? '100%' : '-100%',
    opacity: 0,
  }),
};

const springTransition = {
  type: 'spring',
  stiffness: 300,
  damping: 30,
  mass: 0.8,
};

const arrowBtnStyle = {
  position: 'absolute',
  top: '50%',
  transform: 'translateY(-50%)',
  zIndex: 100000,
  background: 'rgba(255,255,255,0.15)',
  border: 'none',
  borderRadius: '50%',
  width: 48,
  height: 48,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer',
  color: 'white',
  backdropFilter: 'blur(4px)',
};

const FOCUSABLE_SELECTOR = 'button:not(:disabled), [href], input:not(:disabled), select:not(:disabled), textarea:not(:disabled), video, audio, [tabindex]:not([tabindex="-1"])';

const FullScreenGallery = ({ items, initialIndex, onClose, onIndexChange, renderItem }) => {
  const [activeIndex, setActiveIndex] = useState(initialIndex || 0);
  const prevIndexRef = useRef(activeIndex);
  const directionRef = useRef(0);
  const draggedRef = useRef(false);
  const total = items.length;
  const dialogRef = useRef(null);
  const closeButtonRef = useRef(null);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    setActiveIndex(initialIndex || 0);
  }, [initialIndex]);

  useEffect(() => {
    const previouslyFocused = document.activeElement;
    closeButtonRef.current?.focus();

    const getFocusable = () =>
      dialogRef.current ? Array.from(dialogRef.current.querySelectorAll(FOCUSABLE_SELECTOR)) : [];

    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onCloseRef.current?.();
        return;
      }
      if (e.key !== 'Tab') return;
      const focusable = getFocusable();
      if (focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      if (previouslyFocused && typeof previouslyFocused.focus === 'function') {
        previouslyFocused.focus();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (activeIndex !== prevIndexRef.current) {
    directionRef.current = activeIndex > prevIndexRef.current ? 1 : -1;
    prevIndexRef.current = activeIndex;
  }
  const direction = directionRef.current;

  const goTo = (newIndex) => {
    if (newIndex < 0 || newIndex >= total || newIndex === activeIndex) return;
    setActiveIndex(newIndex);
    onIndexChange?.(newIndex);
  };

  const goNext = () => goTo(activeIndex + 1);
  const goPrev = () => goTo(activeIndex - 1);

  const activeItem = items[activeIndex];

  return (
    <div
      ref={dialogRef}
      role="dialog"
      aria-modal="true"
      aria-label="Full-screen media preview"
      onClick={onClose}
      style={{ position: 'fixed', inset: 0, zIndex: 99999, background: 'rgba(0,0,0,0.95)', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}
    >
      <button
        ref={closeButtonRef}
        type="button"
        aria-label="Close preview"
        onClick={onClose}
        style={{ position: 'absolute', top: 40, right: 20, zIndex: 100000, background: 'white', border: 'none', borderRadius: '50%', width: 44, height: 44, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: 'black', boxShadow: '0 4px 12px rgba(0,0,0,0.2)' }}
      >
        <X size={24} strokeWidth={2.5} />
      </button>

      {total > 1 && activeIndex > 0 && (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); goPrev(); }}
          style={{ ...arrowBtnStyle, left: 12 }}
          aria-label="Previous"
        >
          <ChevronLeft size={26} strokeWidth={2.5} />
        </button>
      )}
      {total > 1 && activeIndex < total - 1 && (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); goNext(); }}
          style={{ ...arrowBtnStyle, right: 12 }}
          aria-label="Next"
        >
          <ChevronRight size={26} strokeWidth={2.5} />
        </button>
      )}

      <AnimatePresence initial={false} custom={direction}>
        <motion.div
          key={activeIndex}
          custom={direction}
          variants={slideVariants}
          initial="enter"
          animate="center"
          exit="exit"
          transition={springTransition}
          drag={total > 1 ? 'x' : false}
          dragConstraints={{ left: 0, right: 0 }}
          dragElastic={0.1}
          onDragStart={() => { draggedRef.current = false; }}
          onDrag={(_, info) => {
            if (Math.abs(info.offset.x) > 5) draggedRef.current = true;
          }}
          onDragEnd={(_, info) => {
            if (info.offset.x < -50 && activeIndex < total - 1) goNext();
            else if (info.offset.x > 50 && activeIndex > 0) goPrev();
          }}
          onClick={(e) => e.stopPropagation()}
          className="absolute inset-0 w-full h-full flex items-center justify-center select-none"
          style={{ touchAction: 'pan-y', cursor: total > 1 ? 'grab' : 'default' }}
          whileDrag={{ cursor: 'grabbing' }}
        >
          {renderItem(activeItem, activeIndex)}
        </motion.div>
      </AnimatePresence>

      {total > 1 && (
        <div className="absolute bottom-8 left-0 right-0 z-[100000] flex items-center justify-center pointer-events-none select-none">
          <span className="bg-black/60 backdrop-blur-sm text-white text-[13px] font-bold px-3 py-1 rounded-full">
            {activeIndex + 1} / {total}
          </span>
        </div>
      )}
    </div>
  );
};

FullScreenGallery.propTypes = {
  items: PropTypes.array.isRequired,
  initialIndex: PropTypes.number,
  onClose: PropTypes.func.isRequired,
  onIndexChange: PropTypes.func,
  renderItem: PropTypes.func.isRequired,
};

export default FullScreenGallery;
